import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { accessLevelsApi } from '../accessLevelsApi';
import type { AccessLevel, UserAccessLevel } from '../accessLevelTypes';

/** The sentinel the picker uses for "no level", which is a real choice and not a cleared field. */
const NONE = 'none';

/**
 * Tags one account's clearance across EVERY level-gated module.
 *
 * One picker per gated module, drawn from the registry rather than a list kept here — a module
 * that starts classifying its records grows a picker on this form by existing.
 *
 * **"No level" is an option rather than clearing the picker.** An untagged account is not an
 * account whose clearance nobody has decided; it sits at the weakest rung and reads the least,
 * and the form says that rather than leaving a reader to infer it from an empty box.
 *
 * **No Autogenerate, under the documented exemption**: every picker opens on the account's
 * current rung, so the form is already filled at mount and the contract is met before the
 * button would have anything to do. Each field is also a decision about a named person's
 * access, which is not a thing a generator could fill honestly.
 */
export function EditUserAccessLevelsModal({
  open,
  userId,
  displayName,
  current,
  onClose,
  onSaved,
}: {
  open: boolean;
  userId: string;
  /** The account's own label, so the form names who is being tagged. */
  displayName: string;
  /** One row per gated module, tagged or not — what the card is already showing. */
  current: readonly UserAccessLevel[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [chosen, setChosen] = useState<Record<string, string>>(() =>
    Object.fromEntries(current.map((row) => [row.moduleKey, row.accessLevelId ?? NONE])),
  );

  const [levels, setLevels] = useState<AccessLevel[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    accessLevelsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setLevels(next);
      })
      // One focusValidationError call per catch, in the save path only — a failed options
      // fetch leaves the pickers empty and the server's own refusal to report it.
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingLevels(false);
      });

    return () => controller.abort();
  }, []);

  const canSave = confirmed && !saving && !loadingLevels;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await accessLevelsApi.setUserLevels(userId, {
        /*
          Every gated module the form drew, including the ones left at "No level": the request
          touches only what it names, and this form names them all, so a tag removed here is
          actually removed.
        */
        assignments: current.map((row) => ({
          moduleKey: row.moduleKey,
          accessLevelId: chosen[row.moduleKey] === NONE ? null : (chosen[row.moduleKey] ?? null),
        })),
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not set these access levels.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'user-access-levels' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit access levels"
      size="lg"
      id="user-access-levels-modal"
      confirm={{
        id: 'user-access-levels-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="user-access-levels-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            id="user-access-levels-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {current.length === 0 ? (
          <p className="text-xs text-slate-500">
            No module classifies its records yet, so there is nothing to tag.
          </p>
        ) : null}

        {current.map((row) => (
          <Select
            key={row.moduleKey}
            id={`user-access-levels-${row.moduleKey}`}
            label={row.moduleName}
            description={`The clearance ${displayName} holds when reading ${row.moduleName} records. A stronger rung reads more.`}
            value={chosen[row.moduleKey] ?? NONE}
            onChange={(next) =>
              setChosen((prev) => ({ ...prev, [row.moduleKey]: next || NONE }))
            }
            loading={loadingLevels}
            disabled={saving}
            // The ladder's own order, strongest first — alphabetically "Level 10" sorts above
            // "Level 2", and the order IS the meaning here.
            sort="none"
            clearable={false}
            options={[
              {
                value: NONE,
                label: 'No level',
                sublabel:
                  'The weakest clearance: unclassified records only, plus the bottom rung',
              },
              ...levels.map((level) => ({
                value: level.id,
                label: level.name,
                sublabel: level.description ?? undefined,
                right: <span className="font-mono tabular-nums">Rank {level.rank}</span>,
              })),
            ]}
            error={error?.fieldError('accessLevelId')}
          />
        ))}

        {/*
          The consequence, on the form and before the tick: this is the one control in the
          product that hands somebody records they could not see a moment ago, and it does it
          without touching a single permission.
        */}
        <p className="text-xs text-slate-500">
          A level decides which RECORDS {displayName} can read; their role and permissions decide
          what they can do with the ones they can. Rank 1 is the strongest clearance and reads
          everything. Records above an account&apos;s rung are still listed and counted — the
          name is withheld, and the record cannot be opened.
        </p>
      </div>
    </Modal>
  );
}
